import React, { useCallback, useMemo } from 'react';
import { Badge, Card, Flex, Text } from '@/components/ui';
import { IconAlertTriangle } from '@tabler/icons-react';
import {
  ColumnMapping,
  SelectedFile,
} from '@/pages/data-import/components/types';
import {
  TerminalErrorDisplay,
  TerminalErrorGroup,
} from './TerminalErrorDisplay';

export interface ValidationErrorDetails {
  column_mappings?: {
    [index: string]: {
      [field: string]: string[];
    };
  };
  file_to_form_mappings?: {
    [index: string]: {
      [field: string]: string[];
    };
  };
  subform_relationships?: {
    [index: string]: {
      [field: string]: string[];
    };
  };
  [key: string]: unknown;
}

export interface ValidationErrorsPanelProps {
  errors: ValidationErrorDetails;
  columnMappings: ColumnMapping[];
  selectedFiles: SelectedFile[];
  fileToFormMappings: Array<{ fileId: string; formId: number }>;
}

interface ValidationErrorRow {
  index: number;
  fileId?: string;
  fileName?: string;
  columnName?: string;
  field: string;
  messages: string[];
  type: 'column_mapping' | 'file_to_form_mapping' | 'subform_relationship';
}

// Helper function to format error messages - keep original from backend
const formatErrorMessage = (
  field: string,
  messages: string[]
): { field: string; message: string } => {
  // Keep field name as-is from backend
  const fieldName = field;

  // Keep messages as-is from backend, join multiple messages if needed
  const message = messages.length > 0 ? messages.join(', ') : 'is invalid';

  return {
    field: fieldName,
    message: message,
  };
};

export const ValidationErrorsPanel: React.FC<ValidationErrorsPanelProps> = ({
  errors,
  columnMappings,
  selectedFiles,
  fileToFormMappings,
}) => {
  // Helper function to get file name
  const getFileNameForFileId = useCallback(
    (fileId?: string): string | undefined => {
      if (!fileId) return undefined;
      const file = selectedFiles.find(f => {
        const id =
          f.source === 'uploaded' ? f.file!.name : `csv-${f.csvFile!.id}`;
        return id === fileId;
      });
      if (file) {
        return file.source === 'uploaded'
          ? file.file!.name
          : file.csvFile!.filename;
      }
      return fileId;
    },
    [selectedFiles]
  );

  // Parse errors into structured format
  const parsedErrors = useMemo(() => {
    const errorRows: ValidationErrorRow[] = [];

    // Process column_mappings errors
    if (errors.column_mappings) {
      Object.entries(errors.column_mappings).forEach(
        ([indexStr, fieldErrors]) => {
          const index = parseInt(indexStr, 10);
          const columnMapping = columnMappings[index];

          Object.entries(fieldErrors).forEach(([field, messages]) => {
            errorRows.push({
              index,
              fileId: columnMapping?.fileId,
              fileName: getFileNameForFileId(columnMapping?.fileId),
              columnName: columnMapping?.columnName,
              field,
              messages: Array.isArray(messages) ? messages : [messages],
              type: 'column_mapping',
            });
          });
        }
      );
    }

    // Process file_to_form_mappings errors
    if (errors.file_to_form_mappings) {
      Object.entries(errors.file_to_form_mappings).forEach(
        ([indexStr, fieldErrors]) => {
          const index = parseInt(indexStr, 10);
          const fileMapping = fileToFormMappings[index];

          Object.entries(fieldErrors).forEach(([field, messages]) => {
            errorRows.push({
              index,
              fileId: fileMapping?.fileId,
              fileName: getFileNameForFileId(fileMapping?.fileId),
              field,
              messages: Array.isArray(messages) ? messages : [messages],
              type: 'file_to_form_mapping',
            });
          });
        }
      );
    }

    // Process subform_relationships errors
    if (errors.subform_relationships) {
      Object.entries(errors.subform_relationships).forEach(
        ([indexStr, fieldErrors]) => {
          const index = parseInt(indexStr, 10);

          Object.entries(fieldErrors).forEach(([field, messages]) => {
            errorRows.push({
              index,
              field,
              messages: Array.isArray(messages) ? messages : [messages],
              type: 'subform_relationship',
            });
          });
        }
      );
    }

    return errorRows;
  }, [errors, columnMappings, fileToFormMappings, getFileNameForFileId]);

  // Group errors by file
  const groupedErrors = useMemo(() => {
    const grouped: Record<string, ValidationErrorRow[]> = {};
    parsedErrors.forEach(error => {
      const key = error.fileId || 'unknown';
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(error);
    });
    return grouped;
  }, [parsedErrors]);

  // Convert grouped errors to TerminalErrorDisplay format
  const errorGroups = useMemo((): TerminalErrorGroup[] => {
    return Object.entries(groupedErrors).map(([fileId, fileErrors]) => {
      const firstError = fileErrors[0];
      const fileName = firstError.fileName || fileId || 'Unknown File';

      return {
        id: fileId,
        label: fileName,
        errors: fileErrors.map((error, idx) => {
          const formatted = formatErrorMessage(error.field, error.messages);
          const columnPrefix = error.columnName ? `${error.columnName} → ` : '';

          return {
            id: `${fileId}-${idx}`,
            label: formatted.field,
            message: formatted.message,
            prefix: columnPrefix,
          };
        }),
      };
    });
  }, [groupedErrors]);

  if (parsedErrors.length === 0) {
    return null;
  }

  const totalErrors = parsedErrors.length;

  return (
    <Card>
      <TerminalErrorDisplay
        title={
          <Flex justify="between" align="center">
            <Flex align="center" gap="2">
              <IconAlertTriangle color="var(--red-9)" size={20} />
              <Text size="xsmall" weight="bold">
                Validation Warnings
              </Text>
              <Badge color="error" variant="soft" size="xsmall">
                {totalErrors} error{totalErrors !== 1 ? 's' : ''}
              </Badge>
            </Flex>
          </Flex>
        }
        groups={errorGroups}
      />
    </Card>
  );
};
